import React, { useState } from 'react';
import ModalBody from '../../ModalBody';
import CustomTextInput from '../../../CustomTextInput';
import { useTranslation } from 'react-i18next';
import { Dump } from '../../../../services/apiSLTP/materials/dumps';
import { crudStatus } from '../../../../utils/enums';
import { useCreateDumpMutation, useUpdateDumpMutation } from '../../../../hooks/mutations/useDumpMutation';
import ToggleLine from '../../../ToggleLine';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';

interface ModalProps {
    dump: Dump | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateDumpModal: React.FC<ModalProps> = ({ dump, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();
    const [selectedDesignation, setSelectedDesignation] = useState(dump?.designation ?? '');
    const [selectedCodeProC, setSelectedCodeProC] = useState(dump?.codeProC ?? '');
    const [selectedVolume, setSelectedVolume] = useState(dump?.volume ?? null);
    const [selectedDisplayOrder, setSelectedDisplayOrder] = useState(dump?.displayOrder ?? null);
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(dump?.crudStatus ?? crudStatus.OK);

    const { mutate: mutateDump } = useUpdateDumpMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating dump:', error.message),
    });

    const { mutate: addDump } = useCreateDumpMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating dump:', error.message),
    });

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatus.OK : crudStatus.INACTIVE);
    };

    return (
        <ModalBody
            title={t('dump')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disabledOk={
                !selectedDesignation.length ||
                !selectedCodeProC.length ||
                (selectedDisplayOrder !== null && selectedDisplayOrder < 0) ||
                (selectedVolume !== null && selectedVolume < 0) ||
                selectedVolume === null
            }
            handleOk={() => {
                isEditMode
                    ? mutateDump({
                          dumpId: dump?._id || '',
                          data: {
                              designation: selectedDesignation,
                              crudStatus: selectedCrudStatus,
                              codeProC: selectedCodeProC,
                              volume: selectedVolume ?? 0,
                              displayOrder: selectedDisplayOrder,
                          },
                      })
                    : addDump({
                          designation: selectedDesignation,
                          codeProC: selectedCodeProC,
                          volume: selectedVolume ?? 0,
                          displayOrder: selectedDisplayOrder,
                      });
            }}
        >
            <CustomTextInput value={selectedDesignation} label={t('name')} onChange={setSelectedDesignation} required />
            <CustomTextInput value={selectedCodeProC} label={t('codeProC')} onChange={setSelectedCodeProC} required />
            <ToggleLine checked={selectedCrudStatus === crudStatus.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput
                value={selectedVolume}
                label={t('volume')}
                onChange={(text) => setSelectedVolume(parseInt(text) || null)}
                type="number"
                required
            />
            <CustomTextInput
                value={selectedDisplayOrder}
                label={t('displayOrder')}
                onChange={(text) => setSelectedDisplayOrder(parseInt(text) || null)}
                type="number"
            />
        </ModalBody>
    );
};

export default UpdateDumpModal;
